;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/coverage/:country\",\"regex\":\"^/coverage/([^/]+)$\",\"paramNames\":[\"country\"],\"hasOptionalPrefix\":false},{\"path\":\"/plans/:id\",\"regex\":\"^/plans/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/plans/:id/today\",\"regex\":\"^/plans/([^/]+)/today$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/plan/:token\",\"regex\":\"^/plan/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/assistant\"},{\"path\":\"/auth/callback\"},{\"path\":\"/billing-soon\"},{\"path\":\"/confirm-answer\"},{\"path\":\"/connect\"},{\"path\":\"/coverage\"},{\"path\":\"/dashboard\"},{\"path\":\"/examples/retirement\"},{\"path\":\"/instant-preview\"},{\"path\":\"/login\"},{\"path\":\"/onboarding\"},{\"path\":\"/pay\"},{\"path\":\"/planner-preview\"},{\"path\":\"/pricing\"},{\"path\":\"/privacy\"},{\"path\":\"/review\"},{\"path\":\"/review-preview\"},{\"path\":\"/settings\"},{\"path\":\"/sign-in\"},{\"path\":\"/support\"},{\"path\":\"/value-map\"},{\"path\":\"/your-data\"}],\"isrRoutes\":[\"/coverage/:country\"]}";globalThis["_sentryNextJsVersion"] = "15.5.19";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import * as Sentry from '@sentry/nextjs'
import { SENTRY_DSN } from './sentry-dsn'
import { CONSENT_EVENT, CONSENT_KEY } from './src/lib/analytics/consent'

// Sentry v10 client init lives ONLY here (the old sentry.client.config.ts duplicate was removed to stop
// the double-init). environment + release make regressions attributable to a specific deploy; CI passes
// NEXT_PUBLIC_SENTRY_RELEASE=<git sha> as a build-arg and uploads source maps under that release.
// Replay is analytics-grade observation of the user's screen; it rides the SAME consent switch as
// PostHog/GA (localStorage 'mycio_analytics_consent' === '1' — the two-button banner's key, not
// the legacy acknowledge-only 'mycio_cookie_ok'), read at init for enablement; a mid-session
// REVOKE stops recording immediately below, a mid-session grant takes effect on the next load.
// Error monitoring itself stays on — masked, PII-free crash reports are service operation, not
// tracking.
const _replayConsented = (() => {
  try {
    return typeof window !== 'undefined' && localStorage.getItem(CONSENT_KEY) === '1'
  } catch {
    return false
  }
})()

if (typeof window !== 'undefined') {
  window.addEventListener(CONSENT_EVENT, () => {
    try {
      if (localStorage.getItem(CONSENT_KEY) !== '1') {
        Sentry.getReplay()?.stop()?.catch(() => { /* already stopped */ })
      }
    } catch {
      /* revoke best-effort; replay was consent-gated at init anyway */
    }
  })
}

/** /plan/<token> is an unguessable capability URL; it must not reach Sentry through request URLs
 * or breadcrumbs any more than through PostHog. And QUERY STRINGS go entirely: ?ref= carries a
 * signed referral token, and arbitrary params can carry identity — the PostHog side of this app
 * drops the query for exactly this reason, and the 2026-09-04 review caught the Sentry side
 * scrubbing only the /plan path segment while the query rode through. */
function scrubShareTokens(text: string): string {
  return text.replace(/\/plan\/[A-Za-z0-9_-]{8,}/g, '/plan/[token]')
}

function scrubUrl(url: string): string {
  return scrubShareTokens(url.split('?', 1)[0])
}

Sentry.init({
  dsn: SENTRY_DSN,
  enabled: !!SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
  release: process.env.NEXT_PUBLIC_SENTRY_RELEASE || undefined,
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: _replayConsented ? 0.2 : 0,
  // ALLOW-LIST, SAME AS THE OTHER TWO RUNTIMES (backend error_privacy.py, server
  // instrumentation.ts): this DOM is the household, and an app-thrown error can interpolate the
  // client's own words into its message. Type + stack + scrubbed transaction survive; message
  // VALUES, extra, non-trace contexts and foreign tags do not (2026-09-04 review, blocker 6).
  beforeSend(event) {
    try {
      if (event.request?.url) event.request = { url: scrubUrl(event.request.url) }
      if (typeof event.transaction === 'string') event.transaction = scrubShareTokens(event.transaction)
      delete (event as { extra?: unknown }).extra
      if (event.contexts) {
        const trace = event.contexts.trace
        event.contexts = trace ? { trace } : {}
      }
      delete (event as { tags?: unknown }).tags
      for (const exc of event.exception?.values ?? []) {
        if (exc.value) exc.value = scrubUrl(String(exc.value)).slice(0, 300)
      }
      if (event.message) event.message = scrubUrl(String(event.message)).slice(0, 300)
    } catch {
      return null // fail closed: an unscrubbable event does not ship
    }
    return event
  },
  // TRANSACTIONS ARE NOT ERRORS AND SKIP beforeSend (Sentry v10): a pageload transaction of
  // /plan/<token> carried the raw capability URL — full path AND query — through a channel no
  // scrubber watched (2026-09-04 review, HIGH). Same rules as errors: path-token scrub + the
  // whole query dropped.
  beforeSendTransaction(event) {
    if (event.request?.url) event.request.url = scrubUrl(event.request.url)
    if (typeof event.transaction === 'string') event.transaction = scrubShareTokens(event.transaction)
    return event
  },
  beforeBreadcrumb(crumb) {
    // shape only: category/type/level + a scrubbed url; free-form breadcrumb data can carry
    // fetch bodies and console args — the household, verbatim.
    const url = typeof crumb.data?.url === 'string' ? scrubUrl(crumb.data.url) : undefined
    const status = typeof crumb.data?.status_code === 'number' ? crumb.data.status_code : undefined
    crumb.data = { ...(url ? { url } : {}), ...(status !== undefined ? { status_code: status } : {}) }
    if (typeof crumb.message === 'string') crumb.message = scrubUrl(crumb.message).slice(0, 200)
    return crumb
  },
  // THE PRIVACY SWITCHES, EXPLICIT — the backend's rule (app/error_privacy.py) applied to the
  // browser: this DOM is the household (balances, goals, names), so a replay must ship masked
  // rectangles, never text, and no request may carry identity by default. These are the SDK
  // defaults today; stated here so an SDK default changing cannot silently change what we send.
  sendDefaultPii: false,
  integrations: [Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })],
})

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart
